import { useDocumentHead } from "@/hooks/useDocumentHead.ts";

export function PrivacyPage() {
  useDocumentHead({
    title: "Privacy Policy",
    description: "Read The Secret Trends privacy policy. Learn how we collect, use, and protect your personal information.",
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
      <p className="mt-2 text-sm text-gray-500">Last updated: March 20, 2026</p>

      <div className="mt-8 space-y-6 text-gray-600 leading-relaxed text-sm">
        <p>
          This Privacy Policy describes how The Secret Trends ("the Site", "we", "us", or
          "our") collects, uses, and discloses your personal information when you visit,
          interact with, or make a purchase through our website at thesecrettrends.com.
          For questions, contact us at{" "}
          <a href="mailto:info@thesecrettrends.com" className="text-indigo-600 hover:underline">
            info@thesecrettrends.com
          </a>.
        </p>

        {/* ── Collecting Personal Information ──────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          1. Information We Collect
        </h2>
        <p>
          When you visit the Site, we automatically collect certain information about
          your device, your interaction with the Site, and information necessary to
          process your inquiries. We may also collect additional information if you
          contact us for customer support.
        </p>
        <p className="font-medium text-gray-900">Information collected automatically:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>Device information: browser type and version, operating system, IP address, time zone, device identifiers</li>
          <li>Browsing information: pages visited, products viewed, referring/exit pages, time spent on pages, click patterns, scroll depth</li>
          <li>Location information: approximate geographic location based on IP address</li>
          <li>Technical information: screen resolution, language preference, connection type</li>
        </ul>
        <p className="font-medium text-gray-900">Information you provide:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>Contact information: name, email address, phone number (when voluntarily provided)</li>
          <li>Newsletter subscriptions: email address and communication preferences</li>
          <li>Any other information you choose to submit via contact forms or email</li>
        </ul>

        {/* ── Cookies & Tracking Technologies ──────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          2. Cookies and Tracking Technologies
        </h2>
        <p>
          We use cookies, pixel tags (also known as web beacons or tracking pixels),
          local storage, and similar technologies to collect information about your
          browsing activity and to distinguish you from other users of our Site.
        </p>
        <p className="font-medium text-gray-900">Types of cookies we use:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            <strong>Strictly necessary cookies:</strong> Required for the Site to
            function properly (e.g., session management, security).
          </li>
          <li>
            <strong>Analytics cookies:</strong> Help us understand how visitors
            interact with the Site by collecting and reporting information
            anonymously (e.g., Google Analytics).
          </li>
          <li>
            <strong>Advertising and targeting cookies:</strong> Used to deliver
            advertisements relevant to you and your interests. These cookies track
            your browsing activity across websites and are placed by third-party
            advertising platforms (e.g., Meta Pixel, Google Ads, Pinterest Tag,
            TikTok Pixel).
          </li>
          <li>
            <strong>Performance cookies:</strong> Help us improve our Site by
            measuring performance and errors.
          </li>
        </ul>
        <p>
          Most web browsers allow you to control cookies through their settings. If
          you disable cookies, some features of the Site may not function properly.
          You can also opt out of targeted advertising using the links provided in
          Section 4 below.
        </p>

        {/* ── Third-Party Analytics & Marketing ────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          3. Third-Party Analytics and Marketing Services
        </h2>
        <p>
          We use the following third-party services that may collect, receive, and
          process data about your use of our Site. Each service has its own privacy
          policy governing how it uses your data:
        </p>

        <h3 className="font-semibold text-gray-800">Google Analytics</h3>
        <p>
          We use Google Analytics to analyze website traffic and user behavior. Google
          Analytics uses cookies and collects information such as how often users visit
          the Site, what pages they visit, and what other sites they used prior to
          visiting. Google may use this data to contextualize and personalize ads in
          its advertising network. You can opt out by installing the{" "}
          <a
            href="https://tools.google.com/dlpage/gaoptout"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            Google Analytics Opt-out Browser Add-on
          </a>.
        </p>

        <h3 className="font-semibold text-gray-800">Google Ads (Conversion Tracking & Remarketing)</h3>
        <p>
          We use Google Ads conversion tracking and remarketing tags to measure the
          effectiveness of our advertising campaigns and to show you relevant ads on
          Google's network based on your visit to our Site. Google may use cookies to
          serve ads based on your prior visits. You can opt out of personalized
          advertising by visiting{" "}
          <a
            href="https://adssettings.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            Google Ads Settings
          </a>.
        </p>

        <h3 className="font-semibold text-gray-800">Meta Pixel (Facebook & Instagram)</h3>
        <p>
          We use the Meta Pixel to measure the effectiveness of our advertising on
          Facebook and Instagram, to deliver targeted advertising to you on Meta
          platforms, and to build custom and lookalike audiences. The Meta Pixel
          collects data about your interactions with our Site, including pages visited,
          products viewed, and actions taken. This data is sent to Meta and may be
          combined with other information Meta has about you. You can manage your ad
          preferences at{" "}
          <a
            href="https://www.facebook.com/adpreferences"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            Facebook Ad Preferences
          </a>{" "}
          or opt out via{" "}
          <a
            href="https://www.facebook.com/help/568137493302217"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            Meta's Off-Facebook Activity settings
          </a>.
        </p>

        <h3 className="font-semibold text-gray-800">Pinterest Tag</h3>
        <p>
          We use the Pinterest Tag to track conversions from Pinterest ads, optimize
          ad delivery, and build audiences for future targeting. The tag collects
          information about your interactions with our Site after clicking a Pinterest
          ad. You can adjust your Pinterest ad settings at{" "}
          <a
            href="https://www.pinterest.com/settings/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            Pinterest Privacy Settings
          </a>.
        </p>

        <h3 className="font-semibold text-gray-800">TikTok Pixel</h3>
        <p>
          We use the TikTok Pixel to measure the effectiveness of our TikTok
          advertising campaigns and to deliver targeted ads on TikTok. The pixel
          collects information about your browsing behavior on our Site. You can
          manage your TikTok ad preferences in your{" "}
          <a
            href="https://www.tiktok.com/setting"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            TikTok Privacy Settings
          </a>.
        </p>

        <h3 className="font-semibold text-gray-800">Email Marketing (Brevo)</h3>
        <p>
          We use Brevo (formerly Sendinblue) as our email marketing platform. When you
          subscribe to our newsletter, your email address is stored by Brevo. Our
          emails may contain tracking pixels that allow us to measure open rates and
          click-through rates. You can unsubscribe at any time by clicking the
          "Unsubscribe" link in any of our emails.
        </p>

        <h3 className="font-semibold text-gray-800">Affiliate Tracking</h3>
        <p>
          We participate in affiliate marketing programs. When you click on a product
          link on our Site, a cookie may be set by our affiliate partner (Everflow) to
          track whether a sale is completed. This cookie contains no personal
          information other than the referral data needed to attribute the sale.
        </p>

        {/* ── Advertising & Opt-Out ────────────────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          4. Targeted Advertising and How to Opt Out
        </h2>
        <p>
          We share data with advertising platforms to deliver personalized ads to you
          based on your browsing behavior on our Site. Under some privacy laws, this
          may be considered a "sale" or "sharing" of personal information.
        </p>
        <p>You can opt out of targeted advertising through the following methods:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            <strong>Google:</strong>{" "}
            <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
              Google Ads Settings
            </a>
          </li>
          <li>
            <strong>Meta (Facebook/Instagram):</strong>{" "}
            <a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
              Facebook Ad Preferences
            </a>
          </li>
          <li>
            <strong>Pinterest:</strong>{" "}
            <a href="https://www.pinterest.com/settings/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
              Pinterest Privacy Settings
            </a>
          </li>
          <li>
            <strong>TikTok:</strong>{" "}
            <a href="https://www.tiktok.com/setting" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
              TikTok Privacy Settings
            </a>
          </li>
          <li>
            <strong>Digital Advertising Alliance:</strong>{" "}
            <a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
              DAA Opt-Out Tool
            </a>
          </li>
          <li>
            <strong>Network Advertising Initiative:</strong>{" "}
            <a href="https://optout.networkadvertising.org" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
              NAI Opt-Out Tool
            </a>
          </li>
          <li>
            <strong>European users:</strong>{" "}
            <a href="https://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
              Your Online Choices
            </a>
          </li>
        </ul>
        <p>
          Please note that opting out of targeted advertising does not mean you will
          stop seeing ads entirely — you will still see generic, non-personalized ads.
        </p>

        {/* ── How We Use Information ───────────────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          5. How We Use Your Information
        </h2>
        <p>We use your personal information to:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>Operate, maintain, and improve our Site</li>
          <li>Showcase products and updates relevant to your interests</li>
          <li>Send newsletters and promotional emails (with your consent)</li>
          <li>Measure the performance of our marketing campaigns</li>
          <li>Build audiences for targeted advertising on third-party platforms</li>
          <li>Analyze traffic and user behavior to improve user experience</li>
          <li>Detect and prevent fraud or security threats</li>
          <li>Comply with applicable legal obligations</li>
        </ul>
        <p>
          <strong>Lawful basis (GDPR):</strong> We process your information based on
          your consent (e.g., cookies, newsletter signup), our legitimate interests
          (e.g., analytics, fraud prevention), performance of a contract, and
          compliance with legal obligations.
        </p>

        {/* ── Data Sharing ─────────────────────────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          6. Who We Share Your Data With
        </h2>
        <p>We may share your personal information with the following categories of third parties:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            <strong>Analytics providers:</strong> Google Analytics (website analytics and
            audience reporting)
          </li>
          <li>
            <strong>Advertising platforms:</strong> Google Ads, Meta (Facebook/Instagram),
            Pinterest, TikTok — for conversion tracking, retargeting, and lookalike audience creation
          </li>
          <li>
            <strong>Email marketing:</strong> Brevo — for newsletter delivery and email campaign management
          </li>
          <li>
            <strong>Affiliate networks:</strong> Everflow — for tracking referral commissions on product sales
          </li>
          <li>
            <strong>Hosting and infrastructure:</strong> Our website is hosted on servers
            managed by us; we do not share data with third-party hosting providers
            beyond what is necessary for site operation
          </li>
          <li>
            <strong>Legal requirements:</strong> We may disclose your information if
            required by law, regulation, legal process, or governmental request
          </li>
        </ul>

        {/* ── Data Retention ───────────────────────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          7. Data Retention
        </h2>
        <p>
          We retain your personal information only for as long as necessary to provide
          our services and fulfill the purposes described in this policy. Specifically:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>Analytics data: retained as configured in each analytics platform (typically 14–26 months for Google Analytics)</li>
          <li>Advertising cookies: typically expire within 90 days to 2 years depending on the platform</li>
          <li>Newsletter subscriptions: retained until you unsubscribe</li>
          <li>Contact information: retained as long as necessary to respond to your inquiry</li>
        </ul>
        <p>
          When we no longer need your information, we will securely delete or
          anonymize it.
        </p>

        {/* ── Minors ───────────────────────────────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          8. Children's Privacy
        </h2>
        <p>
          The Site is not intended for individuals under the age of 18. We do not
          knowingly collect personal information from children. If you believe we have
          inadvertently collected information from a child, please contact us so we can
          promptly delete it.
        </p>

        {/* ── Your Rights ──────────────────────────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          9. Your Privacy Rights
        </h2>

        <h3 className="font-semibold text-gray-800">European Economic Area (GDPR)</h3>
        <p>
          If you are a resident of the EEA, you have the right to access, rectify,
          port, restrict, or erase your personal data. You also have the right to
          object to processing and to withdraw consent at any time.
        </p>

        <h3 className="font-semibold text-gray-800">California (CCPA/CPRA)</h3>
        <p>
          If you are a California resident, you have the right to know what personal
          information we collect, request deletion of your data, opt out of the "sale"
          or "sharing" of your personal information, and not be discriminated against
          for exercising these rights. Our use of tracking pixels for targeted
          advertising may constitute a "sale" or "sharing" under the CCPA. You can opt
          out using the methods described in Section 4.
        </p>

        <h3 className="font-semibold text-gray-800">Exercising Your Rights</h3>
        <p>
          To exercise any of these rights, contact us at{" "}
          <a href="mailto:info@thesecrettrends.com" className="text-indigo-600 hover:underline">
            info@thesecrettrends.com
          </a>. We will respond within the timeframe required by applicable law (typically
          30 days for GDPR, 45 days for CCPA).
        </p>

        {/* ── Do Not Track ─────────────────────────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          10. Do Not Track
        </h2>
        <p>
          Some browsers transmit "Do Not Track" (DNT) signals. We currently do not
          respond to DNT signals, as there is no industry-standard approach for
          handling them. However, you can use the opt-out tools listed in Section 4 to
          manage tracking preferences.
        </p>

        {/* ── International Transfers ──────────────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          11. International Data Transfers
        </h2>
        <p>
          Your information may be transferred to and processed in countries outside
          your country of residence, including the United States, where our
          third-party service providers operate. These countries may have different
          data protection laws. When we transfer data internationally, we rely on
          appropriate safeguards such as Standard Contractual Clauses or the service
          provider's participation in recognized data privacy frameworks.
        </p>

        {/* ── Security ─────────────────────────────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          12. Data Security
        </h2>
        <p>
          We use reasonable technical and organizational measures to protect your
          personal information from unauthorized access, loss, misuse, or alteration.
          However, no method of transmission over the internet is 100% secure, and we
          cannot guarantee absolute security.
        </p>

        {/* ── Changes ──────────────────────────────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          13. Changes to This Policy
        </h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in
          our practices, technologies, legal requirements, or for other operational
          reasons. We will post the updated policy on this page with a revised "Last
          updated" date. We encourage you to review this policy periodically.
        </p>

        {/* ── Contact ──────────────────────────────────────────── */}
        <h2 className="text-lg font-semibold text-gray-900 pt-2">
          14. Contact Us
        </h2>
        <p>
          If you have questions or concerns about this Privacy Policy or our data
          practices, please contact us at:
        </p>
        <p>
          The Secret Trends<br />
          Email:{" "}
          <a href="mailto:info@thesecrettrends.com" className="text-indigo-600 hover:underline">
            info@thesecrettrends.com
          </a>
        </p>
      </div>
    </div>
  );
}
